import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";

import ReservationsSetting from "./parts/ReservationsSetting";
import AllowNegativeStockSetting from "./parts/AllowNegativeStockSetting";
import UIButton from "@reusables/UIButton";

import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useUpdateCompanyDefaultsMutation } from "@redux/features/companies/companiesApi";
import { setSettings } from "@redux/features/auth/authSlice";
import { toastError } from "@helpers/utils";
import { isErrorWithMessage } from "@redux/api/query";
import { Company } from "@/types/general";
import { defaultsScheme, DefaultsFormTyping } from "./types";

export default function DefaultsPage() {
    const { t } = useTranslation("", { keyPrefix: "settings.general.defaults" });

    const dispatch = useAppDispatch();

    const company = useAppSelector(state => state.auth.user?.company);
    const companySettings = company?.settings;

    const [updateDefaults, { isLoading }] = useUpdateCompanyDefaultsMutation();

    const form = useForm<DefaultsFormTyping>({
        resolver: zodResolver(defaultsScheme),
        defaultValues: {
            enable_reservations: companySettings?.enable_reservations ?? false,
            allow_negative_stock: companySettings?.allow_negative_stock ?? false
        }
    });

    const { dirtyFields, isDirty } = form.formState;

    // Sync form with company settings when they change
    useEffect(() => {
        if (companySettings) {
            form.reset({
                enable_reservations: companySettings.enable_reservations ?? false,
                allow_negative_stock: companySettings.allow_negative_stock ?? false
            });
        }
    }, [companySettings, form]);

    const onSubmit = form.handleSubmit(async (data) => {
        if (!company?.id) return;

        const updates: Company.Augmentations.Settings.DTO.Update = {
            ...(dirtyFields.enable_reservations && {
                enable_reservations: data.enable_reservations
            }),
            ...(dirtyFields.allow_negative_stock && {
                allow_negative_stock: data.allow_negative_stock
            })
        };

        try {
            const updatedDefaults = await updateDefaults({
                id: company.id,
                ...updates
            }).unwrap();

            dispatch(setSettings(updatedDefaults));
            toast.success(t("responses.success"));

            form.reset({
                ...data,
                ...updatedDefaults
            });
        } catch (e) {
            if (isErrorWithMessage(e)) {
                toastError(e);
            } else {
                console.error("An error occurred while updating defaults:", e);
            }
        }
    });

    return (
        <FormProvider {...form}>
            <div className="levitation-extended">
                <div className="flex flex-row justify-between items-center mb-[44px]">
                    <div className="font-bold text-2xl text-accent">{t("heading")}</div>
                </div>
                <div className="space-y-[16px] mb-[32px]">
                    <ReservationsSetting />
                    <AllowNegativeStockSetting />
                </div>

                <div className="text-center">
                    <UIButton
                        className="w-[200px]"
                        size={"lg"}
                        onClick={onSubmit}
                        disabled={!isDirty}
                        loading={isLoading}
                    >
                        {t("buttons.save")}
                    </UIButton>
                </div>
            </div>
        </FormProvider>
    );
}
